// Phase 4 Fire 1 — back-office Users page (VIEW-ONLY).
// No suspend / no edit / no delete / no reset. Just listing + drill-in.
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";
import { BackofficeHeader } from "./BackofficeMetricsPage";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN",
    { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function StatusPill({ s }) {
  const colour = s === "INCORPORATED" ? "bg-emerald-100 text-emerald-900" :
                  s === "PAID_HANDED_OFF" || s === "IN_FILING"
                    ? "bg-[#C5283F]/10 text-[#7A0E1F]" :
                  s === "INTAKE_COMPLETE" || s === "AWAITING_PAYMENT"
                    ? "bg-amber-100 text-amber-900" :
                  "bg-slate-100 text-slate-700";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${colour}`}>
    {s || "—"}
  </span>;
}

export function BackofficeUsersListPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.backofficeUsers();
        if (!cancelled) setRows(d);
      } catch {
        if (!cancelled) setErr("Could not load users.");
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="min-h-screen bg-[var(--canvas)]" data-testid="users-page">
      <BackofficeHeader user={user} onLogout={() => { logout(); nav("/"); }} active="users" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-1">Users</h1>
        <p className="text-slate-500 mb-8 text-sm">View-only — no actions are taken from this page.</p>

        {err && <div className="text-sm text-red-600 mb-4">{err}</div>}
        {rows === null && !err && <div className="text-sm text-slate-500">Loading…</div>}
        {rows?.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-sm text-slate-500">
            No founders have registered yet.
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Email</th>
                  <th className="text-left px-6 py-3 font-semibold">Name</th>
                  <th className="text-left px-6 py-3 font-semibold">Auth</th>
                  <th className="text-left px-6 py-3 font-semibold">Registered</th>
                  <th className="text-left px-6 py-3 font-semibold">Cases</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`user-row-${u.email}`}>
                    <td className="px-6 py-3 text-slate-900">{u.email}</td>
                    <td className="px-6 py-3 text-slate-700">{u.display_name || "—"}</td>
                    <td className="px-6 py-3 text-slate-700 capitalize">{u.auth_method}</td>
                    <td className="px-6 py-3 text-slate-700">{fmtDate(u.registered_at)}</td>
                    <td className="px-6 py-3 text-slate-700">{u.case_count}</td>
                    <td className="px-6 py-3 text-right">
                      <Link to={`/admin/users/${u.id}`}
                            className="text-xs text-[#C5283F] hover:underline"
                            data-testid={`view-user-${u.email}`}>View →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function BackofficeUserDetailPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.backofficeUserDetail(userId);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setErr("Could not load user.");
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);
  return (
    <div className="min-h-screen bg-[var(--canvas)]" data-testid="user-detail-page">
      <BackofficeHeader user={user} onLogout={() => { logout(); nav("/"); }} active="users" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/admin/users" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Back to users
        </Link>

        {err && <div className="text-sm text-red-600">{err}</div>}
        {!data && !err && <div className="text-sm text-slate-500">Loading…</div>}

        {data && (
          <>
            <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="user-detail-email">
              {data.email}
            </h1>
            <div className="mt-2 mb-8 text-sm text-slate-500 flex items-center gap-3">
              <span>{data.display_name || "—"}</span>
              <span>·</span>
              <span className="capitalize">Auth: {data.auth_method}</span>
              <span>·</span>
              <span>Registered {fmtDate(data.registered_at)}</span>
              <span>·</span>
              <span>{data.case_count} case{data.case_count === 1 ? "" : "s"}</span>
            </div>

            <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">
              Cases
            </h2>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              {data.cases.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  This founder hasn&apos;t started any cases yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold">Case</th>
                      <th className="text-left px-6 py-3 font-semibold">Company</th>
                      <th className="text-left px-6 py-3 font-semibold">Status</th>
                      <th className="text-left px-6 py-3 font-semibold">Created</th>
                      <th className="text-left px-6 py-3 font-semibold">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cases.map((c) => (
                      <tr key={c.case_id}
                          className="border-t border-slate-100 hover:bg-slate-50"
                          data-testid={`user-case-${c.case_id}`}>
                        <td className="px-6 py-3 font-mono text-xs text-slate-900">{c.case_id}</td>
                        <td className="px-6 py-3 text-slate-700">{c.company_name || "—"}</td>
                        <td className="px-6 py-3"><StatusPill s={c.status} /></td>
                        <td className="px-6 py-3 text-slate-700">{fmtDate(c.created_at)}</td>
                        <td className="px-6 py-3 text-slate-700">{fmtDate(c.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
